// coexistence-ui.js — RF Coexistence & Authorization panel (Adv tab).
//
// Aggregate interference over terrain (FSPL + ITU-R P.526 knife-edge diffraction)
// → availability on the GeoNum kernel → GRANT/DENY with a magnitude-trust
// certificate, signed by Web Crypto (auth-keyed, session-attributable).
import { run_coexistence_certified } from './engines.js';
import { signReceipt, verifyReceipt } from './coex-cert.js';
import { getToken } from './api.js';

const EXAMPLE_EMITTERS = [
  { dist_km: 3.0, tx_dbm: 30, gain_db: 0, freq_offset_mhz: 0 },
  { dist_km: 8.0, tx_dbm: 33, gain_db: 6, freq_offset_mhz: 2, obstacle_height_m: 40, obstacle_dist_km: 4 },
  { dist_km: 1.5, tx_dbm: 20, gain_db: 0, freq_offset_mhz: 5 },
];

export function coexistenceMarkup() {
  return `
    <div style="margin-bottom:1rem;">
      <h3 style="margin:0;">RF Coexistence &amp; Authorization</h3>
      <p style="margin:.5rem 0 0;">Aggregate interference over terrain (FSPL + knife-edge diffraction) → availability on the GeoNum kernel → GRANT/DENY with a signed, magnitude-trust-certified receipt. Honest scope: free-space + single knife-edge; capacity-free decision math across dynamic ranges f64 silently corrupts.</p>
    </div>
    <div class="form-grid">
      <div class="field"><label>Victim freq (MHz)</label><input type="number" id="coex-fmhz" value="1500"></div>
      <div class="field"><label>Noise floor (dBm)</label><input type="number" id="coex-noise" value="-110"></div>
      <div class="field"><label>Protection I/N (dB)</label><input type="number" id="coex-prot" value="-6"></div>
      <div class="field"><label>Shadowing σ (dB)</label><input type="number" id="coex-sigma" value="8"></div>
      <div class="field"><label>Adj-channel rej (dB/MHz)</label><input type="number" id="coex-rej" value="20"></div>
      <div class="field"><label>Required availability (9s)</label><input type="number" id="coex-nines" value="5"></div>
    </div>
    <div class="field" style="margin-top:.75rem;">
      <label>Emitters (JSON — dist_km, tx_dbm, gain_db, freq_offset_mhz, [obstacle_height_m, obstacle_dist_km])</label>
      <textarea id="coex-emitters" class="input mono" rows="7" spellcheck="false">${JSON.stringify(EXAMPLE_EMITTERS, null, 2)}</textarea>
    </div>
    <div style="margin-top:.75rem;display:flex;gap:.75rem;align-items:center;">
      <button class="btn btn-primary" id="coex-run"><i class="fas fa-tower-broadcast"></i> Run + Certify</button>
      <span id="coex-status" style="font-size:.8rem;color:var(--text-secondary,#94a3b8);"></span>
    </div>
    <div id="coex-result" style="margin-top:1rem;"></div>
  `;
}

export function initCoexistence() {
  const btn = document.getElementById('coex-run');
  if (btn && !btn._wired) { btn._wired = true; btn.addEventListener('click', runCoex); }
}

async function runCoex() {
  const g = id => document.getElementById(id);
  const status = g('coex-status');
  const out = g('coex-result');
  let emitters;
  try { emitters = JSON.parse(g('coex-emitters').value); }
  catch (e) { status.textContent = 'Invalid emitters JSON: ' + e.message; return; }
  if (!Array.isArray(emitters) || !emitters.length) { status.textContent = 'Provide at least one emitter.'; return; }

  const req = {
    victim_freq_mhz: parseFloat(g('coex-fmhz').value) || 1500,
    noise_floor_dbm: parseFloat(g('coex-noise').value) || -110,
    protection_in_db: parseFloat(g('coex-prot').value) || -6,
    shadowing_sigma_db: parseFloat(g('coex-sigma').value) || 8,
    rejection_db_per_mhz: parseFloat(g('coex-rej').value) || 20,
    required_nines: parseFloat(g('coex-nines').value) || 5,
    emitters,
  };
  status.textContent = 'running…';
  await new Promise(r => setTimeout(r, 0));

  let cert;
  try { cert = JSON.parse(run_coexistence_certified(JSON.stringify(req))); }
  catch (e) { status.textContent = 'engine error: ' + (e.message || e); return; }
  if (cert.error) { status.textContent = cert.error; return; }

  // Sign with the session auth token (attributable provenance).
  let signed = cert, verified = false;
  const tok = getToken() || 'anonymous-session';
  try { signed = await signReceipt(cert, tok); verified = await verifyReceipt(signed, tok); }
  catch (_) { /* Web Crypto unavailable → unsigned receipt */ }

  status.textContent = 'done';
  out.innerHTML = renderCert(signed, verified);
}

function trustChip(label, v) {
  const good = ['EXACT', 'PRECISE', 'VALID'].includes(v);
  const warn = v === 'DEGRADED';
  const c = good ? '#10b981' : warn ? '#f59e0b' : '#ef4444';
  return `<span style="display:inline-flex;gap:4px;padding:.2em .7em;border-radius:9999px;font-size:.72rem;font-weight:600;background:${c}22;border:1px solid ${c}55;color:${c};">${label}: ${v}</span>`;
}

function renderCert(c, verified) {
  const grant = c.decision === 'GRANT';
  const dc = grant ? '#10b981' : '#ef4444';
  const sig = c.signature
    ? `${c.signature.slice(0, 32)}… ${verified ? '<span style="color:#10b981;">✓ verified</span>' : '<span style="color:#ef4444;">✗</span>'}`
    : '(unsigned — Web Crypto unavailable)';
  return `
    <div style="border:1px solid var(--border-subtle,#334155);border-radius:12px;padding:1rem;background:var(--surface-1,#0f172a);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:.6rem;">
        <span style="font-size:1.15rem;font-weight:700;color:${dc};">${c.decision}</span>
        <span style="font-size:.72rem;color:var(--text-secondary,#94a3b8);">${c.n_emitters} emitters · aggregate I/N ${c.in_ratio_db.toFixed(1)} dB</span>
      </div>
      <div style="font-size:.8rem;color:var(--text-secondary,#94a3b8);margin-bottom:.6rem;display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;">
        <span>availability ≈ 1e${c.availability_log10.toFixed(1)}</span>
        ${trustChip('magnitude', c.magnitude_trust)} ${trustChip('mantissa', c.mantissa_trust)}
      </div>
      <div style="font-family:var(--ff-mono,monospace);font-size:.68rem;line-height:1.7;color:var(--text-muted,#64748b);background:var(--code-bg,#020617);border:1px solid var(--border-subtle,#334155);border-radius:8px;padding:.7rem;word-break:break-all;">
        model: ${c.model}/${c.model_version} · engine v${c.engine_version}<br>
        fingerprint: ${c.inputs_fingerprint} <span style="opacity:.6;">${c.fingerprint_algo}</span><br>
        signature: ${sig}<br>
        ${c.signature_algo || ''}
      </div>
    </div>
  `;
}
